import { PureDate } from '@/lib/absolute-datetime'
import { api } from '@/lib/api'
import type { APIResponse } from '@/types/api'
import type { Timesheet, TimesheetListResponse } from '@/types/timesheet'

export interface TimesheetFilters {
	from?: string
	to?: string
	date?: string
	userID?: string
}

export interface UpdateTimesheetPayload {
	date?: string
	lines?: Array<{
		id: string
		start: string
		end: string
		hourTypeID: string
		projectID: string
		text?: string
	}>
	approved?: boolean
}

type UpdateTimesheetLinePayload = {
	id: string
	companyID: string
	timesheetID: string
	hourTypeID: string
	projectID: string
	start: string
	end: string
	text?: string
}

export interface TimesheetToAddDTO {
	id: string
	company: string
	user: string
	date: string
	file: null
	lines: TimesheetLineToAddDTO[]
}

export interface TimesheetLineToAddDTO {
	start: string
	end: string
	hourTypeID: string
	projectID: string
	text: string
	startGeolocation?: string
	endGeolocation?: string
	notes?: string
}

export class TimesheetsService {
	static async getTimesheets(filters: TimesheetFilters = {}, companyID?: string): Promise<Timesheet[]> {
		const params: Record<string, string> = {}

		if (filters.from) params.from = new PureDate(filters.from).toString()
		if (filters.to) params.to = new PureDate(filters.to).toString()
		if (filters.date) params.date = new PureDate(filters.date).toString()
		if (filters.userID) params.userID = filters.userID
		if (companyID) params.companyID = companyID

		const response = await api.get<TimesheetListResponse>('/timesheets', {
			params: Object.keys(params).length > 0 ? params : undefined,
		})

		const apiResponse = response.data

		if (!apiResponse.success || !apiResponse.data) {
			throw new Error(apiResponse.message || 'Error al obtener registros horarios')
		}

		return apiResponse.data
	}

	static async downloadTimesheet(timesheetId: string): Promise<Blob> {
		const response = await api.get('/timesheets/download', {
			params: { id: timesheetId },
			responseType: 'blob',
		})

		return response.data as Blob
	}

	static async exportTimesheetsExcel(filters: {
		from?: string
		to?: string
		userID?: string
	}): Promise<{ blob: Blob; filename: string }> {
		const params: Record<string, string> = {}
		if (filters.from) params.from = new PureDate(filters.from).toString()
		if (filters.to) params.to = new PureDate(filters.to).toString()
		if (filters.userID) params.userID = filters.userID

		const response = await api.get('/timesheets/export-excel', {
			params: Object.keys(params).length > 0 ? params : undefined,
			responseType: 'blob',
			validateStatus: () => true,
		})

		const blob = response.data as Blob
		const contentType = (response.headers['content-type'] as string | undefined) ?? ''

		if (contentType.includes('application/json') || response.status >= 400) {
			const text = await blob.text()
			let message = `HTTP ${response.status}`
			try {
				const parsed = JSON.parse(text) as { message?: string }
				if (parsed.message) message = parsed.message
			} catch {}
			throw new Error(message)
		}

		const disposition = (response.headers['content-disposition'] as string | undefined) ?? ''
		const match = disposition.match(/filename="?([^"]+)"?/i)
		const filename = match?.[1] ?? 'registros-horarios.xlsx'

		return { blob, filename }
	}

	static async updateTimesheetLine(payload: UpdateTimesheetLinePayload): Promise<Timesheet> {
		const response = await api.put<APIResponse<Timesheet>>(`/timesheets/update-line`, payload)
		const apiResponse = response.data

		if (!apiResponse.success || !apiResponse.data) {
			throw new Error(apiResponse.message || 'Error al actualizar la línea')
		}

		return apiResponse.data
	}

	static async deleteTimesheetLine(lineId: string, timesheetId: string, companyId: string): Promise<Timesheet> {
		const response = await api.delete<APIResponse<Timesheet>>(`/timesheets/delete-line`, {
			params: { id: lineId, timesheetID: timesheetId, companyID: companyId },
		})
		const apiResponse = response.data

		if (!apiResponse.success || !apiResponse.data) {
			throw new Error(apiResponse.message || 'Error al eliminar la línea')
		}

		return apiResponse.data
	}

	static async approveTimesheetLine(lineId: string): Promise<Timesheet> {
		const response = await api.post<APIResponse<Timesheet>>(`/timesheets/approve-line`, { id: lineId })
		const apiResponse = response.data

		if (!apiResponse.success || !apiResponse.data) {
			throw new Error(apiResponse.message || 'Error al aprobar la línea')
		}

		return apiResponse.data
	}

	static async addTimesheetLine(payload: {
		companyID: string
		timesheetID: string
		hourTypeID: string
		projectID: string
		start: string
		end: string
		text?: string
	}): Promise<Timesheet> {
		const response = await api.post<APIResponse<Timesheet>>(`/timesheets/create-line`, payload)
		const apiResponse = response.data

		if (!apiResponse.success || !apiResponse.data) {
			throw new Error(apiResponse.message || 'Error al añadir la línea')
		}

		return apiResponse.data
	}

	static async createTimesheet(payload: TimesheetToAddDTO): Promise<Timesheet> {
		const response = await api.post<APIResponse<Timesheet>>(`/timesheets`, payload)
		const apiResponse = response.data

		if (!apiResponse.success || !apiResponse.data) {
			throw new Error(apiResponse.message || 'Error al crear el registro horario')
		}

		return apiResponse.data
	}

	static async deleteTimesheet(timesheetId: string, companyId: string): Promise<void> {
		const response = await api.delete<APIResponse<void>>(`/timesheets`, {
			params: { id: timesheetId, companyID: companyId },
		})
		const apiResponse = response.data

		if (!apiResponse.success) {
			throw new Error(apiResponse.message || 'Error al eliminar el registro horario')
		}
	}
}
